/**
 * Chat page — two rooms (Everyone + Artist Lounge). Room availability comes
 * from /api/chat/eligibility so users who lose their subscription drop out
 * of the lounge automatically.
 */
import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ChatRoom from "@/components/chat/ChatRoom";
import { MessageCircle, Lock } from "lucide-react";

export default function Chat() {
  const { user, loading } = useAuth();
  const [rooms, setRooms] = useState(null);
  const [active, setActive] = useState("everyone");

  useEffect(() => {
    if (!user) return;
    api.get("/chat/eligibility")
      .then((r) => setRooms(r.data.rooms || ["everyone"]))
      .catch(() => setRooms(["everyone"]));
  }, [user]);

  if (loading) return null;
  if (!user) return <Navigate to="/login" replace state={{ next: "/chat" }} />;

  const hasLounge = rooms && rooms.includes("artist-lounge");

  return (
    <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-6" data-testid="chat-page">
      <div className="flex items-center gap-3 mb-4">
        <MessageCircle className="h-5 w-5 text-[#FF4500]" />
        <h1 className="font-display text-3xl md:text-4xl">The Green Room</h1>
        <span className="font-mono text-[10px] uppercase tracking-widest text-gray-500">
          MeloCraft community chat
        </span>
      </div>

      <div className="border border-[#2A2A2A] bg-black flex flex-col md:flex-row min-h-[65vh] md:h-[70vh]">
        {/* Sidebar — room list */}
        <aside className="w-full md:w-52 md:border-r border-b md:border-b-0 border-[#2A2A2A] bg-[#0A0A0A]">
          <RoomButton
            room="everyone"
            active={active === "everyone"}
            onClick={() => setActive("everyone")}
            subtitle="Anyone signed in"
          />
          <RoomButton
            room="artist-lounge"
            active={active === "artist-lounge"}
            onClick={() => hasLounge && setActive("artist-lounge")}
            subtitle={hasLounge ? "Subscribed artists only" : "$2/mo artist plan required"}
            locked={!hasLounge}
          />
        </aside>

        {/* Active room */}
        <div className="flex-1 min-w-0 flex flex-col">
          {active === "artist-lounge" && !hasLounge ? (
            <div className="flex-1 grid place-items-center p-8 text-center">
              <div>
                <Lock className="h-6 w-6 mx-auto mb-3 text-gray-500" />
                <div className="font-display text-lg mb-1">The Artist Lounge is for subscribed artists.</div>
                <div className="font-mono text-xs text-gray-500 max-w-sm mx-auto">
                  Subscribe to the $2/mo artist plan in Studio to unlock this room.
                </div>
              </div>
            </div>
          ) : (
            <ChatRoom key={active} room={active} />
          )}
        </div>
      </div>
    </div>
  );
}

function RoomButton({ room, active, onClick, subtitle, locked }) {
  return (
    <button
      data-testid={`chat-room-tab-${room}`}
      onClick={onClick}
      disabled={locked}
      className={`w-full text-left px-4 py-3 border-b border-[#1a1a1a] transition-colors ${
        active ? "bg-[#FF4500]/10 border-l-2 border-l-[#FF4500]" : "hover:bg-white/[0.03]"
      } ${locked ? "opacity-50 cursor-not-allowed" : ""}`}
    >
      <div className="font-display text-sm flex items-center gap-2">
        #{room} {locked && <Lock className="h-3 w-3" />}
      </div>
      <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mt-0.5">
        {subtitle}
      </div>
    </button>
  );
}
